import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    ChefHat,
    Plus,
    X,
    Sparkles,
    Loader2,
    History,
    Download,
    Trash2,
    Clock,
    Flame,
    Users,
    Utensils,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { UnitPills } from "./UnitPills";
import { downloadMagicMenuPdf } from "../lib/magicMenuPdf";
import DailyMenuBuilderDialog from "./DailyMenuBuilderDialog";
import BrandWizardDialog from "./BrandWizardDialog";
import { useAuth } from "../lib/auth";

/**
 * Magic Chef — l'utente dice cosa ha in cucina (ingredienti + quantità),
 * preme "Genera menu" e l'AI produce un menu coerente di 3-6 piatti.
 * Il menu si può scaricare in PDF e si conserva in cronologia personale.
 */
export default function MagicChefButton({ variant = "primary" }) {
    const [open, setOpen] = useState(false);
    const isHero = variant === "hero";
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="magic-chef-btn"
                className={
                    isHero
                        ? "inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-6 py-3 text-sm font-bold hover:shadow-xl transition shadow-md group"
                        : "inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-4 py-2 text-sm font-bold hover:shadow-lg transition shadow group"
                }
            >
                <ChefHat size={isHero ? 18 : 14} className="group-hover:rotate-[-6deg] transition-transform" />
                <span>Magic Chef</span>
                <Sparkles size={isHero ? 14 : 12} className="text-yellow-200 animate-pulse" />
            </button>
            <MagicChefDialog open={open} onOpenChange={setOpen} />
        </>
    );
}

function MagicChefDialog({ open, onOpenChange }) {
    const [items, setItems] = useState([{ name: "", quantity: "", unit: "g" }]);
    const [diners, setDiners] = useState(4);
    const [style, setStyle] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [quota, setQuota] = useState(null);

    useEffect(() => {
        if (open) {
            setError("");
            setResult(null);
            setShowHistory(false);
            // Carica quota e cronologia
            api.get("/billing/status").then((r) => setQuota(r.data?.quota)).catch(() => {});
            api.get("/magic-chef/history").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
        }
    }, [open]);

    const addRow = () => setItems((p) => [...p, { name: "", quantity: "", unit: "g" }]);
    const removeRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
    const updateRow = (i, patch) =>
        setItems((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const generate = async () => {
        setError("");
        const valid = items
            .map((it) => ({
                name: (it.name || "").trim(),
                quantity: it.quantity ? Number(it.quantity) : null,
                unit: (it.unit || "").trim(),
            }))
            .filter((it) => it.name.length > 0);
        if (valid.length === 0) {
            setError("Aggiungi almeno un ingrediente con il nome.");
            return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post("/magic-chef/generate", {
                items: valid,
                diners: Number(diners) || 4,
                style: style.trim(),
            });
            setResult(data);
            toast.success(`Menu "${data.title}" pronto!`);
            // ricarica quota
            api.get("/billing/status").then((r) => setQuota(r.data?.quota)).catch(() => {});
            api.get("/magic-chef/history").then((r) => setHistory(r.data || [])).catch(() => {});
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    const openHistoryItem = async (id) => {
        try {
            const { data } = await api.get(`/magic-chef/${id}`);
            setResult(data);
            setShowHistory(false);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteHistoryItem = async (id) => {
        if (!window.confirm("Eliminare questo menu dalla cronologia?")) return;
        try {
            await api.delete(`/magic-chef/${id}`);
            setHistory((p) => p.filter((m) => m.id !== id));
            toast.success("Menu eliminato");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const magicLeft = quota
        ? quota.unlimited_ai
            ? "∞"
            : (quota.magic_left_plan || 0) + (quota.extra_magic || 0)
        : "—";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl bg-white p-0 overflow-hidden max-h-[92vh] flex flex-col"
                data-testid="magic-chef-dialog"
            >
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white flex items-center justify-center shrink-0 shadow-lg">
                                <ChefHat size={22} />
                            </div>
                            <div className="min-w-0">
                                <DialogTitle className="font-display text-2xl text-brand-ink leading-tight">
                                    Magic Chef
                                </DialogTitle>
                                <DialogDescription className="text-sm text-brand-muted mt-1">
                                    Dimmi cosa hai in cucina e ti creo un menu su misura.
                                </DialogDescription>
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-[10px] uppercase tracking-wider text-brand-muted">Crediti</div>
                            <div className="font-display text-2xl text-brand-navy" data-testid="magic-quota">
                                {magicLeft}
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto">
                    {result ? (
                        <ResultView result={result} onBack={() => setResult(null)} />
                    ) : showHistory ? (
                        <HistoryView
                            history={history}
                            onOpen={openHistoryItem}
                            onDelete={deleteHistoryItem}
                            onBack={() => setShowHistory(false)}
                        />
                    ) : (
                        <div className="p-6 space-y-5">
                            {error && (
                                <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5" data-testid="magic-error">
                                    {error}
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="eyebrow text-brand-ink/70">Cosa hai in cucina?</label>
                                    <button
                                        type="button"
                                        onClick={() => setShowHistory(true)}
                                        data-testid="magic-history-btn"
                                        className="inline-flex items-center gap-1 text-[12px] text-brand-muted hover:text-brand-ink"
                                    >
                                        <History size={12} /> Cronologia ({history.length})
                                    </button>
                                </div>
                                <div className="space-y-2" data-testid="magic-items">
                                    {items.map((it, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={it.name}
                                                onChange={(e) => updateRow(i, { name: e.target.value })}
                                                placeholder="Ingrediente (es. Pasta, Pomodoro, Mozzarella)"
                                                data-testid={`magic-item-name-${i}`}
                                                className="flex-1 px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                            />
                                            <input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                value={it.quantity}
                                                onChange={(e) => updateRow(i, { quantity: e.target.value })}
                                                placeholder="Qtà"
                                                data-testid={`magic-item-qty-${i}`}
                                                className="w-20 px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                            />
                                            <UnitPills value={it.unit} onChange={(v) => updateRow(i, { unit: v })} testid={`magic-item-unit-${i}`} />
                                            <button
                                                type="button"
                                                onClick={() => removeRow(i)}
                                                disabled={items.length === 1}
                                                className="h-9 w-9 rounded-full text-brand-muted hover:text-brand-danger hover:bg-red-50 flex items-center justify-center disabled:opacity-30 transition"
                                                data-testid={`magic-item-del-${i}`}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={addRow}
                                    data-testid="magic-add-row"
                                    className="mt-2 inline-flex items-center gap-1 text-[12px] text-brand-navy hover:text-brand-gold transition"
                                >
                                    <Plus size={12} /> Aggiungi ingrediente
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="eyebrow text-brand-ink/70 mb-1.5 flex items-center gap-1">
                                        <Users size={11} /> Commensali
                                    </div>
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={diners}
                                        onChange={(e) => setDiners(e.target.value)}
                                        data-testid="magic-diners"
                                        className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                    />
                                </div>
                                <div>
                                    <div className="eyebrow text-brand-ink/70 mb-1.5">Stile / occasione (opzionale)</div>
                                    <input
                                        type="text"
                                        value={style}
                                        onChange={(e) => setStyle(e.target.value)}
                                        placeholder="es. Tradizionale, Veloce, Vegetariano"
                                        data-testid="magic-style"
                                        className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                    />
                                </div>
                            </div>

                            <div className="rounded-xl bg-gradient-to-br from-purple-50 to-orange-50 border border-purple-200/50 px-4 py-3 text-[12px] text-brand-ink/80 leading-relaxed">
                                <strong className="text-purple-700">Magic Chef AI:</strong> assume disponibili sale, olio EVO, pepe, spezie e erbe comuni. Genera 3-6 piatti coerenti con quantità per i commensali. Il menu è salvato in cronologia e scaricabile in PDF.
                            </div>
                        </div>
                    )}
                </div>

                {!result && !showHistory && (
                    <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={generate}
                            disabled={generating}
                            data-testid="magic-generate"
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white text-sm font-bold hover:shadow-lg transition disabled:opacity-60 shadow"
                        >
                            {generating ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" /> L'AI sta creando il menu…
                                </>
                            ) : (
                                <>
                                    <Sparkles size={14} /> Genera menu · 1 credito AI
                                </>
                            )}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ResultView({ result, onBack }) {
    const { user } = useAuth();
    const [dailyOpen, setDailyOpen] = useState(false);
    const [brandOpen, setBrandOpen] = useState(false);

    const handleBrandConfirm = (brand) => {
        downloadMagicMenuPdf({
            ...result,
            restaurant_name: user?.restaurant_name || "",
        }, brand);
    };

    return (
        <div className="p-6 space-y-5" data-testid="magic-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="eyebrow text-purple-600">Menu generato</div>
                    <h2 className="font-display text-3xl text-brand-ink mt-0.5" data-testid="magic-result-title">{result.title}</h2>
                    {result.subtitle && (
                        <p className="text-sm text-brand-muted italic mt-1 max-w-2xl">{result.subtitle}</p>
                    )}
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-brand-muted">
                        <Users size={11} /> per {result.diners} commensali
                        {result.style && <span>· {result.style}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <button
                        type="button"
                        onClick={onBack}
                        className="px-3 py-1.5 rounded-full text-[12px] font-semibold border border-brand-line text-brand-ink hover:bg-brand-cream transition"
                    >
                        ← Nuovo menu
                    </button>
                    <button
                        type="button"
                        onClick={() => setBrandOpen(true)}
                        data-testid="magic-download-pdf"
                        className="inline-flex items-center gap-1.5 rounded-full border border-brand-line text-brand-ink bg-paper px-4 py-1.5 text-[12px] font-bold hover:border-brand-gold/60 hover:text-brand-navy transition"
                    >
                        <Download size={12} /> PDF ricettario
                    </button>
                    <button
                        type="button"
                        onClick={() => setDailyOpen(true)}
                        data-testid="open-daily-menu-builder"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-1.5 text-[12px] font-bold hover:bg-brand-navyLight transition shadow"
                    >
                        <Utensils size={12} /> Crea Piatti del Giorno
                    </button>
                </div>
            </div>

            {result.suggested_pantry?.length > 0 && (
                <div className="rounded-xl bg-amber-50/50 border border-amber-200 px-4 py-3 text-[12px] text-amber-900 leading-relaxed">
                    <strong>Ingredienti dispensa suggeriti:</strong>{" "}
                    {result.suggested_pantry.join(", ")}
                </div>
            )}

            <div className="space-y-4" data-testid="magic-dishes">
                {result.dishes.map((d, i) => (
                    <DishCard key={i} dish={d} />
                ))}
            </div>

            <DailyMenuBuilderDialog
                open={dailyOpen}
                onOpenChange={setDailyOpen}
                magicMenu={result}
            />
            <BrandWizardDialog
                open={brandOpen}
                onOpenChange={setBrandOpen}
                onConfirm={handleBrandConfirm}
                title="Stile del PDF Magic Menu"
                confirmLabel="Scarica PDF"
            />
        </div>
    );
}

function DishCard({ dish }) {
    const totalTime = (Number(dish.prep_time_min) || 0) + (Number(dish.cook_time_min) || 0);
    return (
        <div className="rounded-xl border border-brand-line bg-paper p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="eyebrow text-brand-gold">{dish.course}</div>
                    <h3 className="font-display text-xl text-brand-ink mt-0.5 leading-tight">{dish.name}</h3>
                    {dish.description && <p className="mt-1 text-sm text-brand-ink/80">{dish.description}</p>}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-brand-muted shrink-0">
                    {totalTime > 0 && (
                        <span className="inline-flex items-center gap-1"><Clock size={11} /> {totalTime} min</span>
                    )}
                    {dish.difficulty && (
                        <span className="inline-flex items-center gap-1"><Flame size={11} /> {dish.difficulty}</span>
                    )}
                    {dish.suggested_price_eur != null && (
                        <span className="font-display text-base text-brand-gold">
                            € {Number(dish.suggested_price_eur).toFixed(2).replace(".", ",")}
                        </span>
                    )}
                </div>
            </div>
            {dish.allergens?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {dish.allergens.map((a, i) => (
                        <span key={i} className="text-[10px] rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 font-semibold uppercase tracking-wider">
                            {a}
                        </span>
                    ))}
                </div>
            )}
            {dish.ingredients_used?.length > 0 && (
                <div className="mt-3">
                    <div className="eyebrow text-brand-ink/60 mb-1">Ingredienti</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-brand-ink">
                        {dish.ingredients_used.map((ing, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-brand-gold shrink-0" />
                                <span className="truncate">
                                    {ing.name}
                                    {ing.quantity != null && (
                                        <span className="text-brand-muted ml-1">
                                            · {ing.quantity}{ing.unit ? ` ${ing.unit}` : ""}
                                        </span>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {dish.instructions?.length > 0 && (
                <div className="mt-3">
                    <div className="eyebrow text-brand-ink/60 mb-1">Preparazione</div>
                    <ol className="space-y-1.5 text-[13px] text-brand-ink leading-relaxed">
                        {dish.instructions.map((step, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="font-bold text-brand-gold shrink-0">{i + 1}.</span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}

function HistoryView({ history, onOpen, onDelete, onBack }) {
    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl text-brand-ink">Cronologia Magic Menu</h3>
                <button
                    onClick={onBack}
                    className="text-[12px] text-brand-muted hover:text-brand-ink"
                >
                    ← Torna
                </button>
            </div>
            {history.length === 0 ? (
                <div className="py-10 text-center text-sm text-brand-muted">
                    Nessun menu in cronologia. Genera il tuo primo Magic Menu!
                </div>
            ) : (
                <div className="space-y-2" data-testid="magic-history-list">
                    {history.map((m) => (
                        <div
                            key={m.id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-brand-line bg-paper hover:bg-brand-cream/40 transition"
                        >
                            <ChefHat size={18} className="text-purple-600 shrink-0" />
                            <button
                                type="button"
                                onClick={() => onOpen(m.id)}
                                data-testid={`magic-history-open-${m.id}`}
                                className="flex-1 min-w-0 text-left"
                            >
                                <div className="font-display text-base text-brand-ink truncate">{m.title}</div>
                                <div className="text-[11px] text-brand-muted">
                                    {(m.dishes || []).length} piatti · {new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => onDelete(m.id)}
                                className="h-8 w-8 rounded-full text-brand-muted hover:text-brand-danger hover:bg-red-50 flex items-center justify-center transition"
                                data-testid={`magic-history-del-${m.id}`}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
